import React from 'react';

/**
 * Completely safe router hooks that don't use React Router at all
 * These provide basic navigation functionality using the browser APIs
 */

/**
 * Safe navigate function using browser history API
 */
export function useSafeNavigate() {
  return React.useCallback((to: any, options?: { replace?: boolean; state?: any }) => {
    if (typeof to === 'number') {
      window.history.go(to);
    } else if (typeof to === 'string') {
      // Use pushState/replaceState for client-side navigation
      if (to.startsWith('/') || to.startsWith('http')) {
        if (options?.replace) {
          window.history.replaceState(options?.state || {}, '', to);
        } else {
          window.history.pushState(options?.state || {}, '', to);
        }
        // Trigger a popstate event to update any listeners
        window.dispatchEvent(new PopStateEvent('popstate'));
      } else {
        window.location.href = to;
      }
    }
  }, []);
}

/**
 * Safe location using browser location API
 */
export function useSafeLocation() {
  const [location, setLocation] = React.useState(() => ({
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    state: null,
    key: 'default'
  }));

  React.useEffect(() => {
    const updateLocation = () => {
      setLocation({
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
        state: null,
        key: 'default'
      });
    };

    // Listen for navigation events
    window.addEventListener('popstate', updateLocation);
    window.addEventListener('pushstate', updateLocation);
    window.addEventListener('replacestate', updateLocation);

    return () => {
      window.removeEventListener('popstate', updateLocation);
      window.removeEventListener('pushstate', updateLocation);
      window.removeEventListener('replacestate', updateLocation);
    };
  }, []);

  return location;
}

/**
 * Safe params - parse from URL search params
 */
export function useSafeParams<T extends Record<string, string | undefined> = Record<string, string | undefined>>(): Readonly<T> {
  return React.useMemo(() => {
    // For now, return empty params since we'd need route matching logic
    // In a real implementation, you'd parse the pathname against route patterns
    return {} as Readonly<T>;
  }, []);
}
